import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import {
  LabeledIconChip,
  Link,
  Loader,
  useBoolModalState,
  useModalState,
  useToast,
} from '@app/core';
import { Input, PhoneNumberInput, YupValidators } from '@app/form';
import {
  Participant,
  useCurrentParticipant,
  useMe,
  useUpdateAccount,
  useUpdateParticipant,
} from '@app/api';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { InformationPaneLayout } from './components/InformationPaneLayout';
import { Trans, useTranslation } from 'react-i18next';
import { VerifyEmailViaCodeModal, VerifyPhoneViaCodeModal } from '@app/auth';
import { parsePhoneNumber } from 'libphonenumber-js';

const useStyles = createUseStyles<string, {}, any>((theme: Theme) => ({
  containerLoader: {
    width: '100%',
    background: theme.colors.white,
    minHeight: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  button: {
    ...theme.fonts.label,
    color: theme.colors.blue,
  },
}));

const validationSchema = Yup.object().shape({
  firstName: YupValidators.isValidName().required(),
  lastName: YupValidators.isValidName().required(),
  email: Yup.string().email().required(),
});

export const ContactDetails = () => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { data: me } = useMe();
  const { data: participant, isLoading } = useCurrentParticipant();
  const toast = useToast();
  const { t } = useTranslation();

  const { mutateAsync: updateAccount } = useUpdateAccount(me?.id);
  const { mutateAsync: updateParticipant } = useUpdateParticipant(
    participant?.id,
  );
  const [loginCodeEmail, openCodeModal, closeCodeModal] = useModalState<
    string | undefined
  >(undefined);
  const [
    phoneVerificationOpened,
    openPhoneVerification,
    closePhoneVerification,
  ] = useBoolModalState();

  if (isLoading || !participant) {
    return (
      <div className={classes.containerLoader}>
        <Loader />
      </div>
    );
  }

  const submit = async (values: Participant) => {
    try {
      if (values.email !== me?.email) {
        await updateAccount({
          email: values.email,
        });
      }
      await updateParticipant({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: values.phoneNumber,
      });
      toast.saved();
    } catch (e) {
      toast.exception(e);
      throw e;
    }
  };

  return (
    <>
      <Formik
        initialValues={participant}
        onSubmit={submit}
        validationSchema={validationSchema}
      >
        <InformationPaneLayout
          icon="ri-smartphone-line"
          title={t('participantSpace.profile.contactDetails')}
        >
          <Input
            name="firstName"
            label={t('participantSpace.profile.firstName')}
          />
          <Input
            name="lastName"
            label={t('participantSpace.profile.lastName')}
          />
          <Input
            name="email"
            label={t('participantSpace.profile.email')}
            type="email"
            rightAction={
              !participant.emailValidated ? (
                <Link
                  type="button"
                  className={classes.button}
                  onClick={() => openCodeModal(participant.email)}
                >
                  {t('form.button.verify')}
                </Link>
              ) : (
                <LabeledIconChip
                  chipLabel={t('participantSpace.profile.verified')}
                  labelColor="green"
                  chipIcon="ri-checkbox-circle-fill"
                />
              )
            }
          />
          <PhoneNumberInput
            name="phoneNumber"
            label={t('participantSpace.profile.phoneNumber')}
            rightAction={
              !participant.phoneValidated ? (
                <Link
                  type="button"
                  className={classes.button}
                  onClick={openPhoneVerification}
                >
                  {t('form.button.verify')}
                </Link>
              ) : (
                <LabeledIconChip
                  chipLabel={t('participantSpace.profile.verified')}
                  labelColor="green"
                  chipIcon="ri-checkbox-circle-fill"
                />
              )
            }
          />
        </InformationPaneLayout>
      </Formik>
      <VerifyEmailViaCodeModal
        onRequestClose={closeCodeModal}
        isOpen={!!loginCodeEmail}
        email={loginCodeEmail}
        onSuccess={closeCodeModal}
      />
      <VerifyPhoneViaCodeModal
        isOpen={phoneVerificationOpened}
        onRequestClose={closePhoneVerification}
        phone={participant?.phoneNumber || undefined}
        text={
          <Trans
            i18nKey="participantSpace.profile.phoneVerification"
            values={{
              phone: participant?.phoneNumber
                ? parsePhoneNumber(
                    participant.phoneNumber,
                  )?.formatInternational()
                : '',
            }}
            components={{ br: <br />, b: <strong /> }}
          />
        }
        participantId={participant?.id}
        onSuccess={closePhoneVerification}
      />
    </>
  );
};
